// The opening score of a game: the same number the stage header shows for
// the opening. The game starts level, so the opening's whole change is the
// score at the reader's first move after leaving the book, from the reader's
// side, read through `openingExit` in the review API so the header and this
// table can never disagree.
import { type Color, type Scan } from '@chess/api-model';
import { openingEndOf, openingExit, readerPawns, readerScoreText, scoreForReader, type ReaderScore } from '@chess/api-review';

export type { ReaderScore };

/** Where the opening's score was read and what it is, from the reader's side. */
export interface OpeningScore {
  /** The position after the reader's first move following the book. */
  toPly: number;
  score: ReaderScore;
}

/** A score for the side to move, seen from `reader`'s side. */
export const forReader = scoreForReader;

/** A reader-side score in pawns; mates saturate. */
export const pawns = readerPawns;

/**
 * The opening score, or undefined when the book knew nothing of the game or
 * the scan lacks the position. `exitPly` is the last position the book
 * knows, as the path record stores it; `moves` the game's length.
 */
export function openingScore(scan: Pick<Scan, 'positions'> | undefined, exitPly: number, moves: number, reader: Color): OpeningScore | undefined {
  if (exitPly < 0) return undefined;
  const phases = { openingEnd: openingEndOf(moves, exitPly) };
  const exit = openingExit(scan, phases, moves, reader);
  if (!exit.score) return undefined;
  return { toPly: exit.toPly, score: exit.score };
}

/** The score bar's notation: `+0.30`, `-1.40`, `-M3`, `mated`. */
export function scoreText(score: ReaderScore): string {
  return readerScoreText(score);
}

export type RowScore = { kind: 'score'; score: OpeningScore } | { kind: 'unscanned' } | { kind: 'scanning' };

export interface Row {
  gameId: string;
  playedAt: string;
  reader: Color;
  result: string;
  exitPly: number;
  score: RowScore;
  /** Where "go" opens the game: the position before the move the score was read at, or the book exit when unscanned. */
  goPly: number;
  /** The game open on the review now. */
  current: boolean;
}

/** Where "go" opens a game whose score is known: the position before the reader's move after the book. */
export function goPlyOf(score: OpeningScore | undefined, exitPly: number): number {
  return score ? Math.max(0, score.toPly - 1) : Math.max(0, exitPly);
}

/** Worst score first, then unscanned, newest first within each. */
export function sortRows(rows: Row[]): Row[] {
  const rank = (r: Row) => (r.score.kind === 'score' ? readerPawns(r.score.score.score) : Number.POSITIVE_INFINITY);
  return [...rows].sort((a, b) => rank(a) - rank(b) || b.playedAt.localeCompare(a.playedAt));
}
